<template>
  <div class="app-container">
    <!-- Stats cards -->
    <el-row :gutter="20" style="margin-bottom: 20px">
      <el-col :span="4">
        <el-card shadow="hover">
          <div class="stat-card">
            <div class="stat-value">{{ stats.total_imports }}</div>
            <div class="stat-label">Importy (24h)</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="hover">
          <div class="stat-card">
            <div class="stat-value" style="color: #67c23a">{{ stats.successful }}</div>
            <div class="stat-label">Sukces</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="hover">
          <div class="stat-card">
            <div class="stat-value" style="color: #e6a23c">{{ stats.partial }}</div>
            <div class="stat-label">Częściowe</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="hover">
          <div class="stat-card">
            <div class="stat-value" style="color: #f56c6c">{{ stats.failed }}</div>
            <div class="stat-label">Nieudane</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="hover">
          <div class="stat-card">
            <div class="stat-value" style="color: #f56c6c">{{ stats.anomalies }}</div>
            <div class="stat-label">Anomalie</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="hover">
          <div class="stat-card">
            <div class="stat-value" style="color: #409eff">{{ stats.offers_created }} / {{ stats.offers_updated }}</div>
            <div class="stat-label">Utworzone / Zaktualizowane</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Filters -->
    <el-card style="margin-bottom: 20px">
      <el-row :gutter="10">
        <el-col :span="6">
          <el-select v-model="filters.status" placeholder="Status" clearable @change="loadLogs">
            <el-option label="Sukces" value="success" />
            <el-option label="Częściowy" value="partial" />
            <el-option label="Nieudany" value="failed" />
            <el-option label="Brak XML" value="no_xml" />
          </el-select>
        </el-col>
        <el-col :span="6">
          <el-select v-model="filters.source" placeholder="Źródło" clearable @change="loadLogs">
            <el-option label="IMO" value="imo" />
            <el-option label="Asari" value="asari" />
          </el-select>
        </el-col>
        <el-col :span="6">
          <el-checkbox v-model="filters.anomalies" @change="loadLogs">Tylko anomalie</el-checkbox>
        </el-col>
        <el-col :span="6">
          <el-button type="primary" icon="el-icon-refresh" @click="loadAll">Odśwież</el-button>
        </el-col>
      </el-row>
    </el-card>

    <!-- Import logs table -->
    <el-card>
      <div slot="header">
        <span>Logi importu</span>
      </div>
      <el-table v-loading="loading" :data="logs" border style="width: 100%">
        <el-table-column label="Data" width="160">
          <template slot-scope="scope">
            {{ formatDate(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="Źródło" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.source === 'imo' ? '' : 'warning'" size="small">
              {{ (scope.row.source || 'imo').toUpperCase() }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Użytkownik" min-width="180">
          <template slot-scope="scope">
            {{ scope.row.user ? scope.row.user.email : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="Plik" min-width="160" prop="file_name" />
        <el-table-column label="XML" width="70" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.xml_found ? 'success' : 'info'" size="small">
              {{ scope.row.xml_found ? 'Tak' : 'Nie' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="W XML" width="80" align="center" prop="total_in_xml" />
        <el-table-column label="Utworz." width="80" align="center" prop="offers_created" />
        <el-table-column label="Aktual." width="80" align="center" prop="offers_updated" />
        <el-table-column label="Błędne" width="80" align="center">
          <template slot-scope="scope">
            <span :style="{ color: scope.row.offers_failed > 0 ? '#f56c6c' : '' }">
              {{ scope.row.offers_failed }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="Usunięte" width="80" align="center" prop="offers_deleted" />
        <el-table-column label="Status" width="110" align="center">
          <template slot-scope="scope">
            <el-tag :type="statusType(scope.row.status)" size="small">
              {{ statusLabel(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Błąd" min-width="200">
          <template slot-scope="scope">
            <span v-if="scope.row.error_message" style="color: #f56c6c">
              {{ scope.row.error_message }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-if="pagination.total > 0"
        style="margin-top: 15px; text-align: right"
        :current-page="pagination.current_page"
        :page-size="pagination.per_page"
        :total="pagination.total"
        layout="total, prev, pager, next"
        @current-change="handlePageChange"
      />
    </el-card>
  </div>
</template>

<script>
import IntegrationResource from '@/api/integration';

const integrationResource = new IntegrationResource();

export default {
  name: 'ImportLogs',
  data() {
    return {
      loading: false,
      logs: [],
      stats: {
        total_imports: 0,
        successful: 0,
        partial: 0,
        failed: 0,
        no_xml: 0,
        anomalies: 0,
        offers_created: 0,
        offers_updated: 0,
      },
      filters: {
        status: '',
        source: '',
        anomalies: false,
      },
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
      },
    };
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      await Promise.all([this.loadLogs(), this.loadStats()]);
    },
    async loadLogs() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
        };
        if (this.filters.status) {
          params.status = this.filters.status;
        }
        if (this.filters.source) {
          params.source = this.filters.source;
        }
        if (this.filters.anomalies) {
          params.anomalies = true;
        }
        const response = await integrationResource.getImportLogs(params);
        this.logs = response.data;
        this.pagination.current_page = response.current_page;
        this.pagination.total = response.total;
        this.pagination.per_page = response.per_page;
      } catch (e) {
        this.$message.error('Nie udało się pobrać logów importu');
      } finally {
        this.loading = false;
      }
    },
    async loadStats() {
      try {
        const response = await integrationResource.getImportStats();
        this.stats = response;
      } catch (e) {
        console.error('Failed to load stats', e);
      }
    },
    handlePageChange(page) {
      this.pagination.current_page = page;
      this.loadLogs();
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleString('pl-PL');
    },
    statusType(status) {
      const map = { success: 'success', partial: 'warning', failed: 'danger', no_xml: 'info' };
      return map[status] || 'info';
    },
    statusLabel(status) {
      const map = { success: 'Sukces', partial: 'Częściowy', failed: 'Nieudany', no_xml: 'Brak XML' };
      return map[status] || status;
    },
  },
};
</script>

<style scoped>
.stat-card {
  text-align: center;
}
.stat-value {
  font-size: 24px;
  font-weight: bold;
}
.stat-label {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}
</style>
